/** Referrals: a ServiceRequest that stays open until its outcome is recorded. */
import { EXT_CDS_RULE, EXT_REFERRAL_OUTCOME, EXT_REFERRAL_TRANSPORT, SYS_SNOMED, } from './fhir.js';
import { newId } from './ids.js';
const extsOf = (r) => (r.extension ?? []);
/** A referral's urgency (FHIR 'stat' and 'asap' read as an emergency). */
export const urgencyOf = (sr) => sr.priority === 'stat' || sr.priority === 'asap'
    ? 'emergency'
    : sr.priority === 'urgent'
        ? 'urgent'
        : 'routine';
/** How she travels to the referral site, if recorded. */
export const transportOf = (sr) => extsOf(sr).find((x) => x.url === EXT_REFERRAL_TRANSPORT)?.valueString ?? null;
/** SNOMED CT categories of a ServiceRequest: a referral, or a laboratory test. */
export const SR_REFERRAL = '3457005';
export const SR_LAB = '108252007';
export const isLabOrder = (sr) => !!sr.category?.some((c) => c.coding?.some((x) => x.code === SR_LAB));
/** A laboratory test requested at a visit; open until its result is recorded. */
export function labOrderResource(v) {
    const sr = {
        resourceType: 'ServiceRequest',
        id: (v.makeId ?? newId)(),
        status: 'active',
        intent: 'order',
        category: [{ coding: [{ system: SYS_SNOMED, code: SR_LAB, display: 'Laboratory procedure' }] }],
        priority: 'routine',
        code: { coding: [{ system: 'urn:assist:lab-test', code: v.test }], text: v.label },
        subject: { reference: `Patient/${v.patientId}` },
        ...(v.encounterId ? { encounter: { reference: `Encounter/${v.encounterId}` } } : {}),
        authoredOn: v.nowIso,
    };
    if (v.ruleId)
        sr.extension = [{ url: EXT_CDS_RULE, valueString: v.ruleId }];
    return sr;
}
/** Record a test's result: the request is completed and the result kept with it. */
export function resultLabOrder(sr, result, today) {
    return {
        ...sr,
        status: 'completed',
        note: [...(sr.note ?? []), { text: `Result (${today}): ${result.trim()}` }],
    };
}
export function referralResource(v) {
    const sr = {
        resourceType: 'ServiceRequest',
        id: (v.makeId ?? newId)(),
        status: 'active',
        intent: 'order',
        category: [{ coding: [{ system: SYS_SNOMED, code: '3457005', display: 'Patient referral' }] }],
        priority: v.urgency === 'emergency' ? 'stat' : v.urgency,
        code: { text: v.destination?.trim() ? `Referral to ${v.destination.trim()}` : 'Referral' },
        subject: { reference: `Patient/${v.patientId}` },
        ...(v.encounterId ? { encounter: { reference: `Encounter/${v.encounterId}` } } : {}),
        authoredOn: v.nowIso,
        reasonCode: [{ text: v.reason.slice(0, 500) }],
    };
    const ext = [
        ...(v.ruleId ? [{ url: EXT_CDS_RULE, valueString: `${v.ruleId}@${v.ruleVersion ?? 1}` }] : []),
        ...(v.transport?.trim()
            ? [{ url: EXT_REFERRAL_TRANSPORT, valueString: v.transport.trim().slice(0, 120) }]
            : []),
    ];
    if (ext.length)
        sr.extension = ext;
    return sr;
}
/** Close a referral with what happened. 'not_attended' and 'cancelled' are recorded as revoked. */
export function closeReferral(sr, outcome, today, note) {
    return {
        ...sr,
        status: outcome === 'completed' ? 'completed' : 'revoked',
        note: [...(sr.note ?? []), ...(note?.trim() ? [{ text: note.trim() }] : [])],
        extension: [
            ...extsOf(sr).filter((x) => x.url !== EXT_REFERRAL_OUTCOME),
            {
                url: EXT_REFERRAL_OUTCOME,
                extension: [
                    { url: 'outcome', valueCode: outcome },
                    { url: 'date', valueDate: today },
                ],
            },
        ],
    };
}
export function referralOutcomeOf(sr) {
    const x = extsOf(sr).find((e) => e.url === EXT_REFERRAL_OUTCOME);
    const outcome = x?.extension?.find((e) => e.url === 'outcome')?.valueCode;
    if (!outcome)
        return null;
    return { outcome, date: x?.extension?.find((e) => e.url === 'date')?.valueDate ?? null };
}
export const openReferrals = (srs) => srs
    .filter((s) => s.status === 'active' && !isLabOrder(s))
    .sort((a, b) => a.authoredOn.localeCompare(b.authoredOn));
//# sourceMappingURL=referral.js.map